/**
 * EmailVerificationBanner, D-090 soft-freeze countdown banner.
 *
 * Behavior (per Ean's 2026-06-03 dogfood feedback):
 *   - Shows hours-remaining countdown (48 hours from signup_at).
 *   - Inline 6-digit code input + "Verify" button + "Resend" link.
 *   - Non-blocking until countdown hits 0; gateway returns soft-freeze after.
 *   - Hides as soon as email_verified flips to true (session refresh).
 *
 * After 48 hours the banner stays visible but copy hardens to "Your account is
 * locked until you verify", the actual dispatch deny lives at the MCP gateway
 * layer (Phase 1b wiring), not in the SPA.
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useSession } from '../hooks/useSession.tsx';
import { sendVerificationEmail, verifyEmailOtp, ApiError } from '../lib/api.ts';
import { useToast } from './Toast.tsx';
import { ButtonCluster } from './ButtonCluster.tsx';

const GRACE_HOURS = 48;

function computeHoursRemaining(signupAtIso: string): number {
  const signup = new Date(signupAtIso).getTime();
  if (Number.isNaN(signup)) return GRACE_HOURS;
  const elapsedMs = Date.now() - signup;
  const remainingMs = GRACE_HOURS * 60 * 60 * 1000 - elapsedMs;
  return Math.max(0, Math.ceil(remainingMs / (60 * 60 * 1000)));
}

export function EmailVerificationBanner() {
  const { user, refresh } = useSession();
  const { showToast } = useToast();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  if (user === null || user.email_verified === true) return null;

  const hoursLeft = computeHoursRemaining(user.signup_at);
  const locked = hoursLeft <= 0;

  function handleCodeChange(e: ChangeEvent<HTMLInputElement>) {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digitsOnly);
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      await verifyEmailOtp(code);
      // Phase 1.23 (2026-06-29): one of three sparingly-exciting moments
      // in the app (welcome email + email verified + card saved). Every
      // other success toast stays in period voice.
      showToast({ kind: 'success', message: 'Email verified!' });
      refresh();
      setCode('');
    } catch (err) {
      showToast({
        kind: 'error',
        message:
          err instanceof ApiError
            ? err.message
            : 'Could not verify that code. Try again.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await sendVerificationEmail();
      showToast({
        kind: 'success',
        message: `Verification email sent to ${user!.email}.`,
      });
    } catch (err) {
      showToast({
        kind: 'error',
        message:
          err instanceof ApiError ? err.message : 'Could not send. Try again.',
      });
    } finally {
      setResending(false);
    }
  }

  const headline = locked
    ? 'Your account is locked until you verify'
    : 'Verify your email';
  const subtext = locked
    ? `Enter the 6-digit code we emailed to ${user.email}, or hit Resend.`
    : `${hoursLeft}h left. Enter the 6-digit code we emailed to ${user.email}.`;

  return (
    <div
      className={`verify-banner${locked ? ' verify-banner-locked' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className="verify-banner-text">
        <strong>{headline}</strong>
        <span className="verify-banner-subtext">{subtext}</span>
      </div>
      <form onSubmit={handleVerify} className="verify-banner-form">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          aria-label="6-digit verification code"
          className="verify-banner-input"
          disabled={submitting}
        />
        <ButtonCluster size="sm" gap={6}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={code.length !== 6 || submitting}
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="btn btn-link"
          >
            {resending ? 'Sending…' : 'Resend'}
          </button>
        </ButtonCluster>
      </form>
    </div>
  );
}
